import { isNounVerbedEventName } from "./naming";

/**
 * The code-owned event schema registry (M12 §"Event schema registry +
 * validator"). ONE versioned catalog of every §6 taxonomy event → its
 * required props, so the taxonomy can't rot as milestones add call sites.
 *
 * - `EVENTS` gives every emitter a string constant to import instead of
 *   hardcoding a name (the "stop hardcoding strings" mandate).
 * - `EVENT_REGISTRY` is the source of truth: name → { required, optional }.
 * - `validateEvent` FAILS on an unregistered name or a missing required prop.
 *
 * The registry is gated in CI by `registry.test.ts` (rejects drift) — a new
 * `track("…")` call whose name isn't registered here fails the suite. This
 * module is pure + client-safe (no Node/DB imports) so it loads in both the
 * server tracker and browser beacons.
 *
 * Envelope fields (`distinct_id`, `anon_session_id`, `user_id`, `plan`,
 * `schema_version`, `ts`, `source_surface`, `app_version`) are stamped by
 * `buildEnvelope` and are NOT listed here — the registry governs the
 * event-specific `props` a call site supplies.
 */

export const ANALYTICS_REGISTRY_VERSION = 1;

/** Every registered event name, grouped by §6 funnel stage. Import these — never hardcode. */
export const EVENTS = {
  // Stage A — Acquisition / import
  APP_OPENED: "app_opened",
  INTAKE_VIEWED: "intake_viewed",
  INTAKE_METHOD_OPENED: "intake_method_opened",
  INTAKE_FILE_PICKER_OPENED: "intake_file_picker_opened",
  SHEET_FETCH_STARTED: "sheet_fetch_started",
  SHEET_FETCH_SUCCEEDED: "sheet_fetch_succeeded",
  SHEET_FETCH_FAILED: "sheet_fetch_failed",
  IMPORT_STARTED: "import_started",
  COLUMN_MAPPING_PROPOSED: "column_mapping_proposed",
  COLUMN_MAPPING_CORRECTED: "column_mapping_corrected",
  IMPORT_COMMITTED: "import_committed",
  IMPORT_FAILED: "import_failed",

  // Stage B — Activation (first map)
  MAP_RENDERED_FIRST: "map_rendered_first",
  MAP_STYLED: "map_styled",
  MAP_FILTERED: "map_filtered",

  // Stage C — Aha (first NL query)
  NL_QUERY_SUBMITTED: "nl_query_submitted",
  NL_QUERY_FIRST: "nl_query_first",
  NL_QUERY_SUCCEEDED: "nl_query_succeeded",
  NL_QUERY_FAILED: "nl_query_failed",
  NL_ANSWER_HIGHLIGHTED: "nl_answer_highlighted",
  NL_QUERY_UNDONE: "nl_query_undone",

  // Stage D — Retention / embed loop
  MAP_SAVE_STARTED: "map_save_started",
  MAP_SAVED: "map_saved",
  MAP_SAVE_FAILED: "map_save_failed",
  SHARE_CREATED: "share_created",
  EMBED_SNIPPET_COPIED: "embed_snippet_copied",
  EMBED_VIEWED: "embed_viewed",
  PUBLIC_MAP_VIEWED: "public_map_viewed",
  EMBED_REFERRAL_SIGNUP: "embed_referral_signup",
  USER_SIGNED_UP: "user_signed_up",
  ATTRIBUTION_CLICKED: "attribution_clicked",
  DATASET_REFRESHED: "dataset_refreshed",
  MAP_RETURNED: "map_returned",

  // Stage E — Monetization (paid triggers)
  QUOTA_WARNING_SHOWN: "quota_warning_shown",
  PAYWALL_HIT: "paywall_hit",
  PAYWALL_EMAIL_CAPTURED: "paywall_email_captured",
  UPGRADE_CLICKED: "upgrade_clicked",
  CHECKOUT_STARTED: "checkout_started",
  CHECKOUT_FAILED: "checkout_failed",
  CHECKOUT_EXPIRED: "checkout_expired",
  CHECKOUT_COMPLETED: "checkout_completed",
  SUBSCRIPTION_CANCELED: "subscription_canceled",
  SUBSCRIPTION_CANCEL_REQUESTED: "subscription_cancel_requested",
  SUBSCRIPTION_PAST_DUE: "subscription_past_due",
  PLAN_CHANGED: "plan_changed",
  SUBSCRIPTION_REFUNDED: "subscription_refunded",

  // Experimentation + identity (M12 engine)
  EXPERIMENT_EXPOSED: "experiment_exposed",
  IDENTITY_ALIASED: "identity_aliased",
  CLIENT_EXCEPTION_DIAGNOSED: "client_exception_diagnosed",

  // M10 sync / public API (registered where already emitted)
  CONNECTOR_CONNECT_STARTED: "connector_connect_started",
  CONNECTOR_CONNECTED: "connector_connected",
  CONNECTOR_AUTH_FAILED: "connector_auth_failed",
  SOURCE_BOUND: "source_bound",
  SYNC_FAILED: "sync_failed",
  API_KEY_CREATED: "api_key_created",
  API_REQUEST_MADE: "api_request_made",

  // M11 compliance lifecycle (registered where already emitted)
  ACCOUNT_DELETION_REQUESTED: "account_deletion_requested",
  ACCOUNT_DATA_DELETED: "account_data_deleted",
  DATASET_DELETED: "dataset_deleted",
  GOOGLE_COORDS_EXPIRED: "google_coords_expired",

  // Map analytics subsystem (FAIL-431) — enriched per-map signals. Schema only
  // in P2.1 (FAIL-463); server/client emit lands in P2.2/P2.3/P2.5.
  MAP_VIEW: "map_view",
  EMBED_IMPRESSION: "embed_impression",
  MARKER_INTERACTION: "marker_interaction",

  // Milestone: a map gets its first view; the owner's first-view email is claimed (MAP-176).
  FIRST_VIEW_NOTIFIED: "first_view_notified",
} as const;

export type EventName = (typeof EVENTS)[keyof typeof EVENTS];

export interface EventSpec {
  /** Props without which the event is meaningless for its funnel role — validated. */
  required: readonly string[];
  /** Props a call site MAY carry (documented; not enforced). */
  optional?: readonly string[];
  /** What the event means + where it fires. */
  description: string;
}

/**
 * The canonical §6 catalog. Required props follow the M12 taxonomy (e.g.
 * `map_rendered_first {time_to_first_map_ms, marker_count, style,
 * geocode_strategy}`, `paywall_hit {trigger, quota_type, usage}`,
 * `embed_referral_signup {from_share_slug}`). Props the shipped M0/M5/M6/M10
 * scaffold emits under legacy names are carried as `optional` so a registry
 * entry never invents a value its emitter can't yet supply.
 */
export const EVENT_REGISTRY: Record<string, EventSpec> = {
  // ── Stage A — Acquisition / import ───────────────────────────────────────
  [EVENTS.APP_OPENED]: {
    required: [],
    optional: ["referrer", "utm_source", "utm_medium", "utm_campaign", "is_returning"],
    description: "Top of funnel — a session's first app load.",
  },
  [EVENTS.INTAKE_VIEWED]: {
    required: ["intake_surface"],
    description: "A create-map intake surface was shown — the denominator for method engagement.",
  },
  [EVENTS.INTAKE_METHOD_OPENED]: {
    required: ["intake_surface", "method"],
    description: "A user expanded an intake method card.",
  },
  [EVENTS.INTAKE_FILE_PICKER_OPENED]: {
    required: ["intake_surface"],
    description: "The spreadsheet file picker was opened before a file was selected.",
  },
  [EVENTS.SHEET_FETCH_STARTED]: {
    required: ["intake_surface"],
    description: "A valid Google Sheet link was submitted for preview.",
  },
  [EVENTS.SHEET_FETCH_SUCCEEDED]: {
    required: ["intake_surface", "row_count"],
    description: "A Google Sheet was fetched and parsed for preview.",
  },
  [EVENTS.SHEET_FETCH_FAILED]: {
    required: ["intake_surface", "failure_code"],
    description: "A Google Sheet preview fetch failed.",
  },
  [EVENTS.IMPORT_STARTED]: {
    required: ["source", "intake_surface"],
    optional: ["source_kind"],
    description: "Ingest entry — the time-to-first-map clock starts here.",
  },
  [EVENTS.COLUMN_MAPPING_PROPOSED]: {
    required: [],
    optional: [
      "row_count",
      "auto_detect_confidence_avg",
      "geocode_strategy",
      "column_count",
      "address_column_count",
    ],
    description: "Auto-detected column mapping shown — measures the friction-killer's quality.",
  },
  [EVENTS.COLUMN_MAPPING_CORRECTED]: {
    required: ["columns_corrected_count"],
    description: "User overrode the auto-detected mapping — inverse health signal for auto-detect.",
  },
  [EVENTS.IMPORT_COMMITTED]: {
    required: ["source", "intake_surface"],
    optional: ["row_count", "geo_ok_pct", "provider_mix", "geocode_strategy", "dataset_id"],
    description: "Dataset committed after mapping — the second Activation-funnel step.",
  },
  [EVENTS.IMPORT_FAILED]: {
    required: ["failure_code", "source", "intake_surface"],
    optional: ["source_kind"],
    description: "Import errored — drop-off diagnosis at the top of funnel.",
  },

  // ── Stage B — Activation (first map) ─────────────────────────────────────
  [EVENTS.MAP_RENDERED_FIRST]: {
    required: ["time_to_first_map_ms", "marker_count", "style", "geocode_strategy"],
    optional: ["map_id"],
    description:
      "North-star activation event — once per identity; the literal measurement of the 30-seconds promise.",
  },
  [EVENTS.MAP_STYLED]: {
    required: ["via"],
    optional: ["style", "column"],
    description: "Style applied; `via` separates GUI edits from NL-driven edits.",
  },
  [EVENTS.MAP_FILTERED]: {
    required: ["via"],
    optional: ["filter_field"],
    description: "Filter applied; `via` separates GUI edits from NL-driven edits.",
  },

  // ── Stage C — Aha (first NL query) ───────────────────────────────────────
  [EVENTS.NL_QUERY_SUBMITTED]: {
    required: ["prompt_len"],
    optional: ["intent_hint"],
    description: "An NL query was submitted.",
  },
  [EVENTS.NL_QUERY_FIRST]: {
    required: ["intent", "time_since_first_map_ms"],
    description: "The aha moment — once per identity; correlated against retention + conversion.",
  },
  [EVENTS.NL_QUERY_SUCCEEDED]: {
    required: ["intent"],
    optional: ["confidence", "op_count", "had_fallback"],
    description: "An NL query resolved to applied ops.",
  },
  [EVENTS.NL_QUERY_FAILED]: {
    required: ["failure_code"],
    optional: ["intent"],
    description: "An NL query failed / was out of scope.",
  },
  [EVENTS.NL_ANSWER_HIGHLIGHTED]: {
    required: ["highlighted_row_count"],
    description: "The answer-on-the-map payoff landed.",
  },
  [EVENTS.NL_QUERY_UNDONE]: {
    required: ["within_10s"],
    optional: ["ms_since_apply"],
    description: "Undo-of-an-NL-op — the misinterpretation-rate signal (quick-undo when within_10s).",
  },

  // ── Stage D — Retention / embed loop ─────────────────────────────────────
  [EVENTS.MAP_SAVE_STARTED]: {
    required: ["save_attempt_id", "save_source", "is_first_save"],
    description: "A map persistence attempt reached the server.",
  },
  [EVENTS.MAP_SAVED]: {
    required: [],
    optional: ["is_first_save", "saved_map_count", "map_id", "save_attempt_id", "save_source"],
    description: "Switching-cost begins; the first save triggers lazy auth + identity stitch.",
  },
  [EVENTS.MAP_SAVE_FAILED]: {
    required: ["save_attempt_id", "save_source", "failure_code", "is_first_save"],
    optional: ["http_status"],
    description: "A map persistence attempt failed after reaching the server.",
  },
  [EVENTS.SHARE_CREATED]: {
    required: ["visibility"],
    optional: ["embed_mode", "allow_nl", "map_id"],
    description: "A share/embed artifact was minted.",
  },
  [EVENTS.EMBED_SNIPPET_COPIED]: {
    required: ["embed_mode"],
    optional: ["live", "show_branding", "map_id"],
    description: "Embed activation — the retention hook deployed to a host page.",
  },
  [EVENTS.EMBED_VIEWED]: {
    required: ["share_slug"],
    optional: ["is_interactive", "referrer_host", "source_surface"],
    description: "A map was rendered inside an embed iframe (source_surface=embed).",
  },
  [EVENTS.PUBLIC_MAP_VIEWED]: {
    required: ["share_slug"],
    optional: ["referrer_host"],
    description: "A public map was viewed at its share URL — viral reach.",
  },
  [EVENTS.EMBED_REFERRAL_SIGNUP]: {
    required: ["from_share_slug"],
    description: "The embed → new-user loop closed — a signup attributed to an originating share.",
  },
  [EVENTS.USER_SIGNED_UP]: {
    required: [],
    optional: ["email"],
    description:
      "A new account was created (Clerk user.created, after the local user row is synced) — drives the new-signup Slack notification (PostHog destination).",
  },
  [EVENTS.ATTRIBUTION_CLICKED]: {
    required: ["share_slug"],
    description: "The 'Made with' attribution badge on a shared map was clicked.",
  },
  [EVENTS.DATASET_REFRESHED]: {
    required: [],
    optional: ["refresh_policy", "rows_changed", "dataset_id", "map_id"],
    description: "Live-map retention — a bound dataset refreshed.",
  },
  [EVENTS.MAP_RETURNED]: {
    required: ["days_since_last"],
    optional: ["saved_map_count"],
    description: "Week-N retention cohorts — an owner returned to a saved map.",
  },

  // ── Stage E — Monetization (paid triggers) ───────────────────────────────
  [EVENTS.QUOTA_WARNING_SHOWN]: {
    required: ["quota_type"],
    optional: ["usage", "limit"],
    description: "Pre-gate nudge rendered before a quota is hit (M12-B UI emits this).",
  },
  [EVENTS.PAYWALL_HIT]: {
    required: ["trigger", "quota_type", "usage"],
    optional: ["action", "limit", "plan_code"],
    description:
      "The gate fired — `trigger` is the enumerated paid trigger the conversion funnel segments by.",
  },
  [EVENTS.PAYWALL_EMAIL_CAPTURED]: {
    required: ["trigger", "quota_type", "checkout_attempt_id", "paywall_lead_id"],
    optional: ["plan_code"],
    description:
      "An anonymous visitor explicitly submitted an email to continue a blocked upgrade; email itself stays out of analytics.",
  },
  [EVENTS.UPGRADE_CLICKED]: {
    required: ["trigger"],
    optional: ["plan_code", "checkout_attempt_id"],
    description: "The upgrade CTA on a paywall was clicked.",
  },
  [EVENTS.CHECKOUT_STARTED]: {
    required: ["plan_code", "checkout_attempt_id", "checkout_session_id"],
    optional: ["trigger", "quota_type"],
    description: "Server-confirmed Stripe Checkout Session creation — never emitted before Stripe returns a session.",
  },
  [EVENTS.CHECKOUT_FAILED]: {
    required: ["checkout_attempt_id", "failure_code"],
    optional: ["plan_code", "trigger", "quota_type", "failure_phase", "http_status"],
    description: "A checkout attempt failed before a usable Stripe Checkout Session was created.",
  },
  [EVENTS.CHECKOUT_EXPIRED]: {
    required: ["plan_code"],
    optional: ["checkout_attempt_id", "checkout_session_id", "trigger", "quota_type"],
    description: "Stripe expired an incomplete Checkout Session; server-derived from checkout.session.expired.",
  },
  [EVENTS.CHECKOUT_COMPLETED]: {
    required: ["plan_code"],
    optional: [
      "amount_usd",
      "anon_to_user_migrated",
      "billing_cycle",
      "trigger",
      "quota_type",
      "checkout_attempt_id",
      "checkout_session_id",
      "email",
    ],
    description: "Server-derived from the billing webhook — never client-fired; billing-truthed.",
  },
  [EVENTS.SUBSCRIPTION_CANCELED]: {
    required: ["plan_code"],
    optional: ["days_subscribed", "cancel_via", "reason"],
    description: "Server-derived churn — `cancel_via=portal` proves the self-serve cancel bright line.",
  },
  [EVENTS.SUBSCRIPTION_CANCEL_REQUESTED]: {
    required: ["plan_code"],
    optional: ["reason_option", "reason_text"],
    description:
      "User-initiated in-app cancel request + retention reason, fired when the user completes the cancel flow (distinct from the reconciler-derived terminal `subscription_canceled`).",
  },
  [EVENTS.SUBSCRIPTION_PAST_DUE]: {
    required: ["plan_code"],
    description: "Server-derived involuntary churn (dunning).",
  },
  [EVENTS.PLAN_CHANGED]: {
    required: ["plan_code", "previous_plan_code"],
    description:
      "Server-derived mid-subscription tier change (Stripe customer.subscription.updated with a resolved price change) — the portal upgrade/downgrade path, distinct from checkout_completed's first-time purchase.",
  },
  [EVENTS.SUBSCRIPTION_REFUNDED]: {
    required: ["plan_code"],
    optional: ["amount_usd"],
    description:
      "Server-derived from Stripe charge.refunded — the plan tier that was refunded (pre-downgrade), a revenue-reversal counterpart to checkout_completed.",
  },

  // ── Experimentation + identity (M12 engine) ──────────────────────────────
  [EVENTS.EXPERIMENT_EXPOSED]: {
    required: ["experiment_key", "variant"],
    optional: ["is_control"],
    description: "Fired ONLY on actual exposure of a varied surface — analysis counts exposed users.",
  },
  [EVENTS.IDENTITY_ALIASED]: {
    required: ["anon_session_id", "user_id", "alias_trigger"],
    description: "The anon→user merge alias — historical anon activity reattributes to the account.",
  },
  [EVENTS.CLIENT_EXCEPTION_DIAGNOSED]: {
    required: ["category", "error_name", "route"],
    optional: [
      "source_path",
      "line_number",
      "column_number",
      "document_ready_state",
      "visibility_state",
      "body_child_count",
      "translated_dom_detected",
      "webdriver",
    ],
    description: "Sanitized browser context for unresolved stack-overflow and DOM-mutation exceptions.",
  },

  // ── M10 sync / public API ────────────────────────────────────────────────
  [EVENTS.CONNECTOR_CONNECT_STARTED]: {
    required: ["connector"],
    description: "A live-data connector OAuth flow began.",
  },
  [EVENTS.CONNECTOR_CONNECTED]: {
    required: ["connector"],
    description: "A live-data connector authorized successfully.",
  },
  [EVENTS.CONNECTOR_AUTH_FAILED]: {
    required: ["connector", "error_code"],
    description: "Connector token expired/invalid — the observable reconnect signal.",
  },
  [EVENTS.SOURCE_BOUND]: {
    required: ["connector", "refresh_policy"],
    description: "A dataset was bound to a live source with a refresh policy.",
  },
  [EVENTS.SYNC_FAILED]: {
    required: ["connector", "error_code"],
    description: "A scheduled/manual sync failed.",
  },
  [EVENTS.API_KEY_CREATED]: {
    required: ["scope"],
    description: "A public /v1 API key was issued (scope only — never key material).",
  },
  [EVENTS.API_REQUEST_MADE]: {
    required: ["endpoint", "scope"],
    description: "Analytics ONLY — never a billing input (monetization gates on storage, not calls).",
  },

  // ── M11 compliance lifecycle ─────────────────────────────────────────────
  [EVENTS.ACCOUNT_DELETION_REQUESTED]: {
    required: ["account_id", "trigger"],
    description: "A self-serve/support/sweep account deletion was requested.",
  },
  [EVENTS.ACCOUNT_DATA_DELETED]: {
    required: ["account_id", "deleted_row_count", "tables_purged"],
    description: "The account cascade completed.",
  },
  [EVENTS.DATASET_DELETED]: {
    required: ["dataset_id", "deleted_row_count"],
    description: "A single dataset (and dependents) was deleted.",
  },
  [EVENTS.GOOGLE_COORDS_EXPIRED]: {
    required: ["expired_count"],
    description: "The Google-coord 30-day expiry sweep ran.",
  },

  // ── Map analytics subsystem (FAIL-431) ───────────────────────────────────
  // Enriched per-map signals feeding the materialized rollup (map_insights_daily).
  // Keyed by `map_id` (not share_slug) so the rollup buckets per map, and split
  // by `surface` (viewer|embed). `referrer_host` is the bare host only (never a
  // full URL — same no-PII posture as share_event), null for direct/same-origin,
  // so it's optional. P2.1 is schema-only: no emitter references these yet.
  [EVENTS.MAP_VIEW]: {
    required: ["map_id", "surface"],
    optional: ["referrer_host"],
    description: "Per-map analytics — a hosted viewer load of a map (surface=viewer).",
  },
  [EVENTS.EMBED_IMPRESSION]: {
    required: ["map_id", "surface"],
    optional: ["referrer_host"],
    description: "Per-map analytics — a map rendered inside an embed iframe (surface=embed).",
  },
  [EVENTS.MARKER_INTERACTION]: {
    required: ["map_id", "surface"],
    optional: ["referrer_host"],
    description: "Per-map analytics — a viewer opened/clicked a map marker (fires on either surface).",
  },
  [EVENTS.FIRST_VIEW_NOTIFIED]: {
    required: [],
    optional: ["map_id"],
    description: "Milestone: a map got its first view; the owner's first-view email was claimed (MAP-176).",
  },
};

export interface EventValidationResult {
  ok: boolean;
  /** Why validation failed (unset when ok). */
  error?: string;
  /** Required props that were absent (unset when ok). */
  missing?: string[];
}

/** True when `name` is a registered event in the canonical catalog. */
export function isRegisteredEvent(name: string): name is EventName {
  return Object.hasOwn(EVENT_REGISTRY, name);
}

/** Every registered event name (the catalog's key set). */
export function listRegisteredEvents(): string[] {
  return Object.keys(EVENT_REGISTRY);
}

/**
 * Validate an event against the registry. FAILS on:
 *   - a name that isn't `noun_verbed` snake_case,
 *   - an unregistered event name, or
 *   - a missing required prop (undefined/absent).
 * Extra props are allowed (they're forward-compatible enrichment).
 */
export function validateEvent(eventName: string, props: Record<string, unknown>): EventValidationResult {
  if (!isNounVerbedEventName(eventName)) {
    return { ok: false, error: `Event name "${eventName}" is not noun_verbed snake_case` };
  }
  const spec = EVENT_REGISTRY[eventName];
  if (!spec) {
    return { ok: false, error: `Unregistered event "${eventName}" — add it to EVENT_REGISTRY` };
  }
  const missing = spec.required.filter((key) => props[key] === undefined);
  if (missing.length > 0) {
    return {
      ok: false,
      error: `Event "${eventName}" is missing required prop(s): ${missing.join(", ")}`,
      missing,
    };
  }
  return { ok: true };
}
